import React, { useState } from "react";
import { View, Text, StyleSheet, Image, TouchableOpacity, TextInput, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

const MessageThread = ({ thread, onPress }) => {
	const theme = useTheme();
	const styles = useStyles();

	return (
		<TouchableOpacity style={styles.threadItem} onPress={onPress}>
			<Image source={{ uri: thread.avatar }} style={styles.avatar} />
			{thread.online && <View style={styles.onlineIndicator} />}
			<View style={styles.threadInfo}>
				<View style={styles.threadHeader}>
					<Text style={styles.userName}>{thread.name}</Text>
					<Text style={styles.timestamp}>{thread.lastMessage.time}</Text>
				</View>
				<View style={styles.lastMessage}>
					<Text style={styles.messagePreview} numberOfLines={1}>
						{thread.lastMessage.text}
					</Text>
					{thread.unreadCount > 0 && (
						<View style={styles.unreadBadge}>
							<Text style={styles.unreadCount}>{thread.unreadCount}</Text>
						</View>
					)}
				</View>
			</View>
		</TouchableOpacity>
	);
};

const Messages = ({ messages = [], members = [] }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedThread, setSelectedThread] = useState(null);
	const theme = useTheme();
	const styles = useStyles();

	// Sample message threads (in real app, this would come from messages prop)
	const messageThreads = [
		{
			id: 1,
			name: "Sarah Chen",
			avatar: "https://i.pravatar.cc/150?img=5",
			online: true,
			lastMessage: {
				text: "I just finished reading The Midnight Library!",
				time: "2m ago",
			},
			unreadCount: 2,
		},
		{
			id: 2,
			name: "Mike Johnson",
			avatar: "https://i.pravatar.cc/150?img=3",
			online: false,
			lastMessage: {
				text: "What did you think about the ending?",
				time: "1h ago",
			},
			unreadCount: 0,
		},
		{
			id: 3,
			name: "Emma Wilson",
			avatar: "https://i.pravatar.cc/150?img=4",
			online: true,
			lastMessage: {
				text: "Let's discuss it at the next book club meeting",
				time: "3h ago",
			},
			unreadCount: 1,
		},
	];

	const filteredThreads = messageThreads.filter((thread) => thread.name.toLowerCase().includes(searchQuery.toLowerCase()));

	return (
		<View style={styles.container}>
			<View style={styles.searchBar}>
				<Ionicons name="search-outline" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
				<TextInput style={styles.searchInput} placeholder="Search messages..." placeholderTextColor={theme.colors.textSecondary} value={searchQuery} onChangeText={setSearchQuery} />
				<TouchableOpacity style={styles.newMessageButton}>
					<Ionicons name="create-outline" size={24} color={theme.colors.primary} />
				</TouchableOpacity>
			</View>

			<ScrollView style={styles.threadList}>
				{filteredThreads.map((thread) => (
					<MessageThread key={thread.id} thread={thread} onPress={() => setSelectedThread(thread)} />
				))}
			</ScrollView>

			{selectedThread && (
				<View style={styles.chatOverlay}>
					{/* Chat interface would go here */}
					<TouchableOpacity style={styles.closeButton} onPress={() => setSelectedThread(null)}>
						<Ionicons name="close" size={24} color={theme.colors.textSecondary} />
					</TouchableOpacity>
				</View>
			)}
		</View>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
		},
		searchBar: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.md,
			marginBottom: theme.spacing.md,
			...theme.shadows.sm,
		},
		searchIcon: {
			marginRight: theme.spacing.sm,
		},
		searchInput: {
			flex: 1,
			...theme.typography.body,
			color: theme.colors.text,
		},
		newMessageButton: {
			marginLeft: theme.spacing.sm,
			padding: theme.spacing.xs,
		},
		threadList: {
			flex: 1,
		},
		threadItem: {
			flexDirection: "row",
			alignItems: "center",
			backgroundColor: theme.colors.systemBackground.primary,
			padding: theme.spacing.md,
			borderRadius: theme.borderRadius.md,
			marginBottom: theme.spacing.sm,
			...theme.shadows.sm,
		},
		avatar: {
			width: 50,
			height: 50,
			borderRadius: 25,
			marginRight: theme.spacing.sm,
		},
		onlineIndicator: {
			position: "absolute",
			left: 44,
			top: 44,
			width: 14,
			height: 14,
			borderRadius: 7,
			backgroundColor: theme.colors.success,
			borderWidth: 2,
			borderColor: theme.colors.systemBackground.primary,
		},
		threadInfo: {
			flex: 1,
		},
		threadHeader: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.xs,
		},
		userName: {
			...theme.typography.headline,
			color: theme.colors.text,
		},
		timestamp: {
			...theme.typography.caption1,
			color: theme.colors.textSecondary,
		},
		lastMessage: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
		},
		messagePreview: {
			flex: 1,
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			marginRight: theme.spacing.sm,
		},
		unreadBadge: {
			backgroundColor: theme.colors.primary,
			borderRadius: theme.borderRadius.pill,
			paddingHorizontal: 6,
			paddingVertical: 2,
			minWidth: 20,
			alignItems: "center",
		},
		unreadCount: {
			...theme.typography.caption2,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		chatOverlay: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: theme.colors.systemBackground.primary,
			zIndex: 1000,
		},
		closeButton: {
			position: "absolute",
			top: theme.spacing.md,
			right: theme.spacing.md,
			padding: theme.spacing.sm,
		},
	});
};

export default Messages;
